import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Cake, PartyPopper, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'celebrations_seen';

/**
 * Global celebrations layer:
 *  - Polls /api/dashboard/celebrations-today on mount
 *  - Fires confetti + shows a floating banner whenever there are birthdays / anniversaries
 *  - Repeats a small confetti burst every ~2.5 minutes while the user is on the app
 *  - Tracks "seen" celebrations per day in localStorage so the BIG burst+banner only happens once
 */
const CelebrationsOverlay = () => {
  const [data, setData] = useState({ birthdays: [], anniversaries: [], date: '' });
  const [showBanner, setShowBanner] = useState(false);
  const repeatTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/dashboard/celebrations-today`);
      setData(r.data || { birthdays: [], anniversaries: [], date: '' });
    } catch { /* silent — never break the app */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Big burst from both sides
  const bigBurst = () => {
    const colors = ['#7c3aed', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];
    confetti({ particleCount: 80, angle: 60, spread: 65, origin: { x: 0, y: 0.7 }, colors, scalar: 1.1, ticks: 220 });
    confetti({ particleCount: 80, angle: 120, spread: 65, origin: { x: 1, y: 0.7 }, colors, scalar: 1.1, ticks: 220 });
    setTimeout(() => {
      confetti({ particleCount: 120, spread: 100, startVelocity: 30, origin: { x: 0.5, y: 0.4 }, colors, scalar: 1.2, ticks: 300 });
    }, 300);
  };

  // Subtle ongoing sparkle
  const lightSparkle = () => {
    confetti({
      particleCount: 24,
      spread: 70,
      startVelocity: 25,
      origin: { x: Math.random() * 0.8 + 0.1, y: 0.1 },
      colors: ['#7c3aed', '#ec4899', '#f59e0b', '#10b981'],
      scalar: 0.8,
      ticks: 150,
      gravity: 0.7,
    });
  };

  useEffect(() => {
    const total = (data.birthdays?.length || 0) + (data.anniversaries?.length || 0);
    if (!total || !data.date) return;

    // Run big burst once per day per browser
    let seen = {};
    try { seen = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { /* */ }

    if (seen.date !== data.date) {
      // First view today — celebrate hard
      setShowBanner(true);
      bigBurst();
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: data.date })); } catch { /* */ }
    }

    // Light sparkle every 3 minutes while user is around
    repeatTimer.current = setInterval(lightSparkle, 3 * 60 * 1000);
    return () => { if (repeatTimer.current) clearInterval(repeatTimer.current); };
  }, [data]);

  const total = (data.birthdays?.length || 0) + (data.anniversaries?.length || 0);
  if (!total) return null;

  return (
    <>
      {/* Floating dismissible banner */}
      {showBanner && (
        <div
          className="fixed bottom-5 right-5 z-[100] max-w-sm bg-white rounded-2xl shadow-2xl border border-violet-100 overflow-hidden animate-in slide-in-from-bottom-3"
          data-testid="celebrations-banner"
        >
          <div className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-rose-500 text-white p-4 flex items-center gap-3">
            <PartyPopper className="h-6 w-6 flex-shrink-0 animate-pulse" />
            <div className="flex-1">
              <p className="font-bold text-sm leading-tight">Today's celebrations</p>
              <p className="text-[11px] opacity-90">{total} reason{total > 1 ? 's' : ''} to cheer at the office</p>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="bg-white/15 hover:bg-white/25 rounded-full p-1 transition"
              data-testid="celebrations-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-3 space-y-2 max-h-[260px] overflow-y-auto">
            {data.birthdays.map(p => (
              <CelebRow
                key={`b-${p.employee_id}`}
                photo={p.photo_url}
                name={p.employee_name}
                code={p.employee_code}
                line={`Happy Birthday! 🎂  Turning ${p.turning_age}`}
                tone="rose"
                icon={<Cake className="h-4 w-4 text-rose-600" />}
                onClick={() => { bigBurst(); }}
              />
            ))}
            {data.anniversaries.map(p => (
              <CelebRow
                key={`a-${p.employee_id}`}
                photo={p.photo_url}
                name={p.employee_name}
                code={p.employee_code}
                line={`${p.years} year${p.years > 1 ? 's' : ''} with the team! 🎉`}
                tone="violet"
                icon={<PartyPopper className="h-4 w-4 text-violet-600" />}
                onClick={() => { bigBurst(); }}
              />
            ))}
          </div>
          <div className="px-3 pb-2">
            <button
              onClick={bigBurst}
              className="w-full text-[11px] text-violet-600 hover:bg-violet-50 py-1 rounded transition"
              data-testid="celebrations-replay"
            >
              🎊 Pop more confetti
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const CelebRow = ({ photo, name, code, line, tone, icon, onClick }) => {
  const toneCfg = tone === 'rose'
    ? { hoverBg: 'hover:bg-rose-50', avatarBg: 'bg-gradient-to-br from-rose-200 to-rose-300', avatarText: 'text-rose-800' }
    : { hoverBg: 'hover:bg-violet-50', avatarBg: 'bg-gradient-to-br from-violet-200 to-violet-300', avatarText: 'text-violet-800' };
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 text-left p-2 rounded-lg ${toneCfg.hoverBg} transition`}
    >
      {photo ? (
        <img src={`${API}${photo}`} alt={name} className="h-10 w-10 rounded-full object-cover" />
      ) : (
        <div className={`h-10 w-10 rounded-full ${toneCfg.avatarBg} flex items-center justify-center text-sm font-bold ${toneCfg.avatarText}`}>
          {(name || '?').charAt(0)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm text-gray-900 truncate">{name} <span className="text-[10px] text-gray-400 font-mono">· {code}</span></p>
        <p className="text-[11px] text-gray-600 truncate flex items-center gap-1">{icon}{line}</p>
      </div>
    </button>
  );
};

export default CelebrationsOverlay;
